import { type Clinic } from "./clinics.type";

export const enum NotificationTypes {
  "APPOINTMENT" = 1,
  "MESSAGE" = 2,
  "REPORT" = 3,
  "INTAKE_FORM" = 4,
  "TASK" = 5,
  "NOAH_HISTORY_SYNCED" = 6,
  "TOUCHPOINT" = 7,
  "AMBIENT_SCRIBE" = 8,
  "COLLECTION" = 9,
  // A teammate @mentioned you in a chart note, a task or a task comment (TEAM_MENTIONS_FEATURE).
  "MENTION" = 10
}

export interface BaseNotification {
  id: number;
  title: string;
  description: string;
  is_read: boolean;
  provider: number;
  clinic: Clinic;
  is_active: boolean;
  created_at: string;
}

export interface AppointmentNotification extends BaseNotification {
  type: 1;
}

// Patient text routing (INBOX_TEXT_ROUTING_FEATURE): why this MESSAGE notification
// reached the user. Absent on notifications written before the feature.
export type MessageRoutingReason =
  | "assigned"
  | "following"
  | "reassigned"
  | "wants_reschedule";

export interface MessageNotification extends BaseNotification {
  type: 2;
  extra: {
    // Newer notifications carry `patient_id`; older ones only `patient`. Read
    // through `messageNotificationPatientId`.
    patient_id?: number;
    patient?: number;
    routing_reason?: MessageRoutingReason;
    clinic_name?: string;
    // Present for "following": who owns the conversation.
    owner_name?: string;
    // Texts not delivered (TEXTS_NOT_DELIVERED_FEATURE): a staff-typed text that
    // failed. Read through `isSmsNotDeliveredExtra`; absent on every other
    // MESSAGE notification.
    kind?: "sms_not_delivered";
    message_id?: number;
    reason_code?: string;
    reason_category?: string;
    reason_label?: string;
    reason_action?: string;
    phone_type_updated?: boolean;
    /** The patient's name, as the backend writes it into the title. */
    patient_name?: string;
  };
}

export type SmsNotDeliveredExtra = MessageNotification["extra"] & {
  kind: "sms_not_delivered";
};

export const isSmsNotDeliveredExtra = (
  extra: Partial<MessageNotification["extra"]> | null | undefined
): extra is SmsNotDeliveredExtra => extra?.kind === "sms_not_delivered";

export interface ReportNotification extends BaseNotification {
  type: 3;
  extra: {
    provider_id: number;
    expires_utc_epoch: number;
  } & (
    | { csv_title: string; csv_download_url: string }
    | { xlsx_title: string; xlsx_download_url: string }
  );
}

export interface IntakeFormNotification extends BaseNotification {
  type: 4;
  extra: {
    form_ids: number[];
    patient_id: number;
    submitted_by_id: number;
    intake_form_request_id: number;
  };
}

export interface TaskNotification extends BaseNotification {
  type: 5;
  extra: TaskNotificationExtra | TeamTaskNotificationExtra;
}

/** "Task was assigned to you by …": the serialized task plus `patient_id`. */
export interface TaskNotificationExtra {
  kind?: undefined;
  patient_id: number | null;
  task: number;
  id: number;
}

/**
 * "Omar sent a task to Chicago North PCCs" (TEAMS_FEATURE): a team whose alert mode rings the bell.
 * `unclaimed` is as of sending; the row reads the task itself for the current state.
 */
export interface TeamTaskNotificationExtra {
  kind: "team_task";
  staff_task_id: number;
  staff_task_title: string;
  team_id: number;
  team_name: string;
  team_color: string;
  created_by_id: number | null;
  created_by_name: string;
  patient_id?: number;
  patient_name: string | null;
  unclaimed: boolean;
}

export const isTeamTaskExtra = (
  extra: TaskNotification["extra"]
): extra is TeamTaskNotificationExtra => extra.kind === "team_task";

export interface NoahSyncedNotification extends BaseNotification {
  type: 6;
  extra: {
    patient_id: number | null;
    provider_id: number;
  };
}

export interface TouchpointNotification extends BaseNotification {
  type: 7;
  extra: {
    patient_id: number | null;
    fax_id: number | null;
    touchpoint_id: number | null;
    error: string | null;
  };
}

export interface AmbientScribeNotification extends BaseNotification {
  type: 8;
  extra: {
    patient_id: number | null;
    scribe_id: number | null;
    chart_note_id: number | null;
    // Present only on "Insights generated" notifications — used to distinguish
    // them from scribe SOAP/summary notifications (which share type 8).
    insight_count?: number;
    // Present only on Sales Coach "Sales review ready" notifications (SN-19585),
    // which reuse type 8. None of the four scribe senders set it, so it is the
    // discriminator for a sales review — see sales-review-notification.util.ts.
    review_id?: number | null;
    // The appointment the sales review is about, sent alongside `review_id`.
    // Verified against a live payload (see features/sales-coach/CLAUDE.md); it is
    // what lets the bell row link straight to the appointment page without a
    // lookup. Do NOT delete it as "unused" because no serializer here names it.
    appointment_id?: number | null;
    patient_first_name?: string | null;
    patient_last_name?: string | null;
  };
}

export interface CollectNotification extends BaseNotification {
  type: 9;
  extra: {
    collection_record_id: number;
    patient_id: number | null;
    sale_id: number | null;
    // Present only on the dispute-submitted event; null when a cadence finished.
    dispute_id: number | null;
  };
}

/** Fields every mention notification carries in `extra` (TEAM_MENTIONS_FEATURE). */
interface MentionNotificationExtraBase {
  mention_id: number;
  snippet: string;
  created_by_id: number;
  created_by_name: string;
  /** Set when the row reached this person through a team @mention (TEAMS_FEATURE). */
  team_id?: number;
  team_name?: string;
  team_color?: string;
  // In-app only: `title`/`description` (what a phone push shows) carry no patient details.
  patient_id: number | null;
  patient_name: string | null;
}

export interface ChartNoteMentionNotificationExtra
  extends MentionNotificationExtraBase {
  /** Absent on rows written before task mentions existed: treat as "chart_note". */
  kind?: "chart_note";
  chart_note_id: number;
  chart_note_type: number;
  /** The follow-up task ("Make it a task"), once created. */
  task_id: number | null;
}

export interface TaskMentionNotificationExtra
  extends MentionNotificationExtraBase {
  /** "task": the task description; "task_comment": one of its comments. */
  kind: "task" | "task_comment";
  staff_task_id: number;
  staff_task_title: string;
  staff_task_comment_id?: number | null;
}

export type MentionNotificationExtra =
  | ChartNoteMentionNotificationExtra
  | TaskMentionNotificationExtra;

export const isTaskMentionExtra = (
  extra: MentionNotificationExtra
): extra is TaskMentionNotificationExtra =>
  extra.kind === "task" || extra.kind === "task_comment";

export interface MentionNotification extends BaseNotification {
  type: 10;
  extra: MentionNotificationExtra;
}

export type Notification =
  | AppointmentNotification
  | MessageNotification
  | ReportNotification
  | IntakeFormNotification
  | TaskNotification
  | NoahSyncedNotification
  | TouchpointNotification
  | AmbientScribeNotification
  | CollectNotification
  | MentionNotification;

export type NotificationSocketResponse = {
  type: "new.user.notification";
  message: Notification;
};

export type NotificationFilters = {
  provider: number;
  clinic?: number;
  type?: NotificationTypes | null;
  is_read?: boolean;
  is_active?: boolean;
  limit?: number;
  offset?: number;
};
